import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
import { api, ApiError } from '../../api.ts';
import { CardSetup, cardLabel, type SavedCard } from '../../components/CardSetup.tsx';
import { PAYMENT_ERRORS, getStripe } from '../../stripeClient.ts';
import { TopBar } from '../../components/TopBar.tsx';
import { formatEuros, missionHours } from '../../format.ts';
import type { Mission } from '../../types.ts';

const COMMISSION_RATE = 0.1;

export function Payment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mission, setMission] = useState<Mission | null>(null);
  const [card, setCard] = useState<SavedCard | null | 'loading'>('loading');
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ mission: Mission }>(`/missions/${id}`).then(async (res) => {
      setMission(res.mission);
      const saved = await api.get<{ card: SavedCard | null }>(`/payments/org/card?organizationId=${res.mission.organization_id}`);
      setCard(saved.card);
    });
  }, [id]);

  async function confirmAndPay() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ scheduled: boolean; authorizeAt?: string; requiresAction?: boolean; clientSecret?: string }>(`/payments/missions/${id}/payment-intent`);
      // La banque demande une authentification (3D Secure) : Stripe affiche sa fenêtre.
      if (res.requiresAction && res.clientSecret) {
        const stripe = await getStripe();
        const result = await stripe?.handleNextAction({ clientSecret: res.clientSecret });
        if (!stripe || result?.error) {
          setError(result?.error?.message ?? 'L’authentification de la carte n’a pas abouti.');
          return;
        }
      }
      await api.post(`/missions/${id}/confirm`);
      navigate(`/resto/missions/${id}`);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      if (code === 'CARD_DECLINED') setChanging(true);
      setError(PAYMENT_ERRORS[code] ?? "La réservation du paiement n'a pas pu être faite. Réessayez.");
    } finally {
      setBusy(false);
    }
  }

  if (!mission) {
    return (
      <div className="app-shell">
        <TopBar title="Paiement" back />
        <div className="screen-content empty-state">Chargement…</div>
      </div>
    );
  }

  const hours = missionHours(mission.starts_at, mission.ends_at);
  const extraAmountCents = Math.round(mission.hourly_rate_cents * hours);
  const commissionCents = Math.round(extraAmountCents * COMMISSION_RATE);
  const totalCents = extraAmountCents + commissionCents;

  return (
    <div className="app-shell">
      <TopBar title="Confirmer & payer" back />
      <div className="screen-content">
        {mission.assigned_first_name && (
          <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 800 }}>{mission.assigned_first_name} {mission.assigned_last_name}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{mission.title}</div>
            </div>
          </div>
        )}

        <div>
          <div style={{ fontSize: 14, fontWeight: 800, padding: '0 2px 8px' }}>Détail du montant</div>
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>Rémunération extra ({hours}h × {formatEuros(mission.hourly_rate_cents)})</span>
              <span style={{ fontWeight: 700 }}>{formatEuros(extraAmountCents)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>Frais de service Mon Extra</span>
              <span style={{ fontWeight: 700 }}>{formatEuros(commissionCents)}</span>
            </div>
            <hr className="divider" />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 800 }}>Total à régler</span>
              <span style={{ fontSize: 18, fontWeight: 800 }}>{formatEuros(totalCents)}</span>
            </div>
          </div>
        </div>

        <div>
          <div style={{ fontSize: 14, fontWeight: 800, padding: '0 2px 8px' }}>Carte bancaire</div>
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {card === 'loading' && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Chargement…</div>}
            {card && card !== 'loading' && !changing && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}><CreditCard size={15} style={{ verticalAlign: -2 }} /> {cardLabel(card)}</div>
                <button className="btn btn-secondary btn-sm" onClick={() => setChanging(true)}>Changer</button>
              </div>
            )}
            {(card === null || changing) && (
              <CardSetup
                organizationId={mission.organization_id}
                onSaved={(c) => { setCard(c); setChanging(false); setError(null); }}
                onCancel={card && card !== 'loading' ? () => setChanging(false) : undefined}
              />
            )}
          </div>
        </div>

        <div style={{ background: 'var(--accent-tint)', borderRadius: 'var(--radius-md)', padding: '13px 15px', fontSize: 12.5, fontWeight: 600, color: 'var(--accent-dark)', lineHeight: 1.6 }}>
          Vous n'êtes débité qu'à la fin de la mission, une fois les heures validées. Par précaution, le montant est simplement bloqué sur votre carte 48 heures avant le début (sans débit), pour garantir l'extra.
        </div>

        {error && <div className="error-banner">{error}</div>}

        <button className="btn btn-primary" onClick={confirmAndPay} disabled={busy || !card || card === 'loading' || changing}>
          <CreditCard size={17} />
          {busy ? 'Confirmation…' : 'Confirmer et réserver'}
        </button>
        <div style={{ textAlign: 'center', fontSize: 11.5, fontWeight: 600, color: 'var(--text-tertiary)' }}>
          Paiement sécurisé par Stripe · Mon Extra ne voit jamais votre numéro de carte
        </div>
      </div>
    </div>
  );
}
